/**
 * CONTROLS OVERLAY
 *
 * Displays world overview stats, legend, and controls help.
 */

import { useState } from "react";
import { useWorldStore } from "@/stores/worldStore";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useUIStore } from "@/stores/uiStore";
import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut, Move, Mouse, RotateCcw, Grid, Tag, Edit, AlertCircle, CheckCircle, HelpCircle, Plus } from "lucide-react";

export function ControlsOverlay() {
  const { projects, agents, zones, addZone } = useWorldStore();
  const { getActiveWorkspaceBackendId, ensureWorkspaceReady } = useWorkspaceStore();
  const { zoom, setZoom, resetCamera, showGrid, toggleGrid, showZoneLabels, toggleZoneLabels, editMode, toggleEditMode } = useUIStore();
  const [isAddingZone, setIsAddingZone] = useState(false);

  const handleAddZone = async () => {
    setIsAddingZone(true);

    let workspaceId: number | string | null = getActiveWorkspaceBackendId();
    if (!workspaceId) {
      workspaceId = await ensureWorkspaceReady();
    }
    // Fallback: use workspaceId from an already-loaded project
    if (!workspaceId && projects.length > 0) {
      workspaceId = projects[0].workspaceId ?? null;
    }
    if (!workspaceId) {
      setIsAddingZone(false);
      alert("Select or create a workspace first.");
      return;
    }
    const zoneColors = [
      "#3b82f6",
      "#a855f7",
      "#22c55e",
      "#f59e0b",
      "#ef4444",
    ];
    const newZone = {
      name: `New Zone ${zones.length + 1}`,
      color: zoneColors[zones.length % zoneColors.length],
      startX: Math.floor(Math.random() * 4),
      startY: Math.floor(Math.random() * 4),
      width: 2,
      height: 2,
    };
    await addZone(newZone, workspaceId);
    setIsAddingZone(false);
  };

  const healthyCount = projects.filter((p) => p.status === "healthy").length;
  const degradedCount = projects.filter((p) => p.status === "degraded").length;
  const failingCount = projects.filter((p) => p.status === "failing").length;
  const busyAgents = agents.filter((a) => a.state === "busy").length;
  const attentionAgents = agents.filter((a) => a.attentionState && a.attentionState !== "none").length;

  return (
    <>
      {/* Colony Overview Card */}
      <div className="absolute top-4 left-4 z-40 rounded-lg p-3"
           style={{
             background: 'rgba(26,26,30,0.85)',
             backdropFilter: 'blur(24px) saturate(1.3)',
             border: '1px solid rgba(255,255,255,0.08)',
             boxShadow: '0 16px 48px rgba(0,0,0,0.6), 0 4px 12px rgba(0,0,0,0.4)',
           }}
           data-testid="card-stats-overview">
        <div className="flex flex-col gap-2">
          <div className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">
            Colony Overview
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-[#F3F4F6]" data-testid="text-project-count">{projects.length}</div>
              <div className="text-[11px] text-[#6B7280]">Projects</div>
            </div>
            <div className="w-px h-8 bg-[rgba(255,255,255,0.08)]" />
            <div className="text-center">
              <div className="text-2xl font-bold text-[#F3F4F6]" data-testid="text-agent-count">{agents.length}</div>
              <div className="text-[11px] text-[#6B7280]">Agents</div>
            </div>
            <div className="w-px h-8 bg-[rgba(255,255,255,0.08)]" />
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-500">{busyAgents}</div>
              <div className="text-[11px] text-[#6B7280]">Busy</div>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-[rgba(255,255,255,0.08)] flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-500/10 text-green-400">
              {healthyCount} healthy
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-500/10 text-amber-400">
              {degradedCount} degraded
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-500/10 text-red-400">
              {failingCount} failing
            </span>
          </div>
          {attentionAgents > 0 && (
            <div className="flex items-center gap-2 pt-2 border-t border-[rgba(255,255,255,0.08)]">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <span className="text-[11px] text-amber-400">{attentionAgents} agents need attention</span>
            </div>
          )}
        </div>
      </div>

      {/* Legend Card — bottom-left corner */}
      <div className="absolute bottom-4 left-4 z-40 rounded-lg p-3"
           style={{
             background: 'rgba(26,26,30,0.85)',
             backdropFilter: 'blur(24px) saturate(1.3)',
             border: '1px solid rgba(255,255,255,0.08)',
             boxShadow: '0 16px 48px rgba(0,0,0,0.6), 0 4px 12px rgba(0,0,0,0.4)',
           }}
           data-testid="card-legend">
        <div className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-2">
          Legend
        </div>
        <div className="space-y-1.5 text-[11px] text-[#9CA3AF]">
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded-sm bg-green-500" />
            <span>Healthy</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded-sm bg-amber-500" />
            <span>Degraded</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 rounded-sm bg-red-500" />
            <span>Failing</span>
          </div>
          <div className="h-px bg-[rgba(255,255,255,0.06)] my-2" />
          <div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-slate-400" />
              <span>Idle Agent</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span>Busy Agent</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-3 h-3 rounded-full bg-purple-500" />
              <span>Queued Agent</span>
            </div>
          </div>
          <div className="h-px bg-[rgba(255,255,255,0.06)] my-2" />
          <div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-green-500" />
              <span>Completed</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <AlertCircle className="w-3 h-3 text-red-500" />
              <span>Stuck/Error</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <HelpCircle className="w-3 h-3 text-amber-500" />
              <span>Human Needed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Help Hints Card — top-right corner */}
      <div className="absolute top-4 right-4 z-40 rounded-lg p-2.5"
           style={{
             background: 'rgba(26,26,30,0.7)',
             backdropFilter: 'blur(16px)',
             border: '1px solid rgba(255,255,255,0.06)',
           }}
           data-testid="card-controls-help">
        <div className="flex flex-col gap-1.5 text-[11px] text-[#6B7280]">
          <div className="flex items-center gap-1.5">
            <Move className="w-3 h-3 text-[#4B5563]" />
            <span>Drag to pan</span>
          </div>
          <div className="flex items-center gap-1.5">
            <ZoomIn className="w-3 h-3 text-[#4B5563]" />
            <span>Scroll to zoom</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Mouse className="w-3 h-3 text-[#4B5563]" />
            <span>Click to select</span>
          </div>
        </div>
      </div>

      {/* Controls Toolbar */}
      <div className="absolute bottom-4 right-4 flex items-center gap-1.5 z-40 rounded-lg px-2 py-1.5"
           style={{
             background: 'rgba(26,26,30,0.85)',
             backdropFilter: 'blur(24px) saturate(1.3)',
             border: '1px solid rgba(255,255,255,0.08)',
           }}>
        <Button
          size="sm"
          variant="outline"
          onClick={handleAddZone}
          disabled={isAddingZone}
          className="gap-1"
          data-testid="button-add-zone"
        >
          <Plus className="w-4 h-4" />
          Add Zone
        </Button>
        <div className="w-px h-5 bg-[rgba(255,255,255,0.08)]" />
        <Button size="icon" variant="outline" onClick={() => setZoom(zoom * 1.2)} data-testid="button-zoom-in" aria-label="Zoom in">
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="outline" onClick={() => setZoom(zoom * 0.8)} data-testid="button-zoom-out" aria-label="Zoom out">
          <ZoomOut className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="outline" onClick={resetCamera} data-testid="button-reset-camera" aria-label="Reset camera">
          <RotateCcw className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="outline" onClick={toggleGrid} className={showGrid ? "bg-accent" : ""} data-testid="button-toggle-grid" aria-label="Toggle grid" aria-pressed={showGrid}>
          <Grid className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="outline" onClick={toggleZoneLabels} className={showZoneLabels ? "bg-accent" : ""} data-testid="button-toggle-labels" aria-label="Toggle zone labels" aria-pressed={showZoneLabels}>
          <Tag className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant={editMode ? "default" : "outline"}
          onClick={toggleEditMode}
          data-testid="button-toggle-edit-mode"
          aria-label="Toggle edit mode"
          aria-pressed={editMode}
        >
          <Edit className="w-4 h-4" />
        </Button>
      </div>
    </>
  );
}
